/**
 * Reusable search component with debounce and controlled/uncontrolled support
 */
import clsx from 'clsx';
import type { ChangeEvent, KeyboardEvent, ReactElement } from 'react';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { IoClose, IoSearch } from 'react-icons/io5';

import { useDebounce } from '@uidotdev/usehooks';

import Spinner from '@/components/Spinner/Spinner';

export interface SearchProps {
  /** Called with debounced query value */
  onSearch: (query: string) => void;
  /** Called immediately on every keystroke (before debounce) */
  onChangeImmediate?: (query: string) => void;
  /** Debounce delay in milliseconds */
  debounceMs?: number;
  /** Input placeholder text */
  placeholder?: string;
  /** Accessible label for the input */
  ariaLabel?: string;
  /** Show loading indicator */
  isLoading?: boolean;
  /** Controlled value - when provided, component is controlled */
  value?: string;
  /** Default value for uncontrolled mode */
  defaultValue?: string;
  /** Called when search is cleared */
  onClear?: () => void;
  /** Disable the input */
  disabled?: boolean;
  /** Auto-focus on mount */
  autoFocus?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Input id attribute */
  id?: string;
  /** Input name attribute */
  name?: string;
  /** Extra keydown handling (e.g. list navigation); runs after built-in Escape-to-clear */
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  /** id of the listbox this input controls (enables combobox semantics) */
  ariaControls?: string;
  /** id of the active option in the controlled listbox */
  ariaActiveDescendant?: string;
}

const Search = forwardRef<HTMLInputElement, SearchProps>(
  (
    {
      onSearch,
      onChangeImmediate,
      debounceMs = 250,
      placeholder = 'Search…',
      ariaLabel = 'Search',
      isLoading = false,
      value,
      defaultValue = '',
      onClear,
      disabled = false,
      autoFocus = false,
      className,
      id,
      name,
      onKeyDown,
      ariaControls,
      ariaActiveDescendant,
    },
    ref,
  ): ReactElement => {
    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = useState<string>(defaultValue);

    // Keep uncontrolled state aligned if defaultValue changes
    useEffect(() => {
      if (!isControlled) {
        setInternalValue(defaultValue);
      }
    }, [defaultValue, isControlled]);

    const query = isControlled ? value : internalValue;

    // Debounced value for expensive operations
    const debouncedQuery = useDebounce(query, debounceMs);

    // Held in a ref so an inline onSearch prop can't retrigger this effect every render.
    const onSearchRef = useRef(onSearch);
    useEffect(() => {
      onSearchRef.current = onSearch;
    });

    useEffect(() => {
      onSearchRef.current(debouncedQuery);
    }, [debouncedQuery]);

    const setQuery = useCallback(
      (next: string) => {
        if (!isControlled) {
          setInternalValue(next);
        }
        onChangeImmediate?.(next);
      },
      [isControlled, onChangeImmediate],
    );

    const clear = useCallback(() => {
      setQuery('');
      onSearchRef.current('');
      onClear?.();
    }, [onClear, setQuery]);

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        setQuery(e.target.value);
      },
      [setQuery],
    );

    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape' && query) {
          e.preventDefault();
          // Keep enclosing dialogs from treating this Escape as "close"
          e.stopPropagation();
          clear();
          return;
        }
        onKeyDown?.(e);
      },
      [clear, query, onKeyDown],
    );

    const showClear = Boolean(query) && !disabled;

    return (
      <div className={clsx('relative', className)}>
        {isLoading ? (
          <div className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2">
            <Spinner size="sm" />
          </div>
        ) : (
          <IoSearch className="text-ash pointer-events-none absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2" />
        )}
        <input
          ref={ref}
          id={id}
          name={name}
          type="text"
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label={ariaLabel}
          role={ariaControls ? 'combobox' : undefined}
          aria-controls={ariaControls}
          aria-expanded={ariaControls ? true : undefined}
          aria-autocomplete={ariaControls ? 'list' : undefined}
          aria-activedescendant={ariaActiveDescendant}
          disabled={disabled}
          autoFocus={autoFocus}
          autoComplete="off"
          spellCheck={false}
          className={clsx(
            'bg-slate border-storm w-full rounded-lg border py-2.5 pr-10 pl-10',
            'text-pearl placeholder:text-ash',
            'focus:border-aether focus:ring-aether/50 focus:ring-1 focus:outline-none',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'transition-colors',
          )}
        />
        {showClear && (
          <button
            type="button"
            onClick={clear}
            className="text-ash hover:text-pearl absolute top-1/2 right-3 -translate-y-1/2 p-0.5 transition-colors"
            aria-label="Clear search"
            title="Clear"
          >
            <IoClose className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  },
);

Search.displayName = 'Search';

export default Search;
